import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FlowNode } from '../../../types/flowBuilder';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { getNodeColors } from '../utils/nodeColors';

const ClaudeChatNode: React.FC<NodeProps> = ({ data, selected }) => {
  const config = data.config || {};
  const colors = getNodeColors('claude_chat');
  
  return (
    <div className={`
      px-4 py-3 bg-gradient-to-r ${colors.gradient.from} ${colors.gradient.to}
      border-2 transition-all duration-200 ${
        selected ? `${colors.border.selected} shadow-xl scale-105` : `${colors.border.default} shadow-lg`
      }
      rounded-lg min-w-[200px] max-w-[300px]
    `}>
      <Handle
        type="target"
        position={Position.Left}
        className={`w-3 h-3 bg-white border-2 ${colors.handle}`}
        style={{ left: -6 }}
      />
      
      {/* Header */}
      <div className="flex items-center space-x-3">
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
            <ChatBubbleLeftRightIcon className="w-5 h-5 text-white" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className={`text-sm font-semibold ${colors.text.primary} truncate`}>
              Anthropic Claude
            </h3>
            {/* Provider Badge */}
            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-white bg-opacity-30 rounded text-white">
              CLAUDE
            </span>
          </div>
          <p className={`text-xs ${colors.text.secondary} truncate`}>
            {config.model || 'claude-3-sonnet-20240229'}
          </p>
        </div>

        {/* Status indicator */}
        <div className="flex-shrink-0">
          <div className={`w-3 h-3 rounded-full ${
            data.hasError ? 'bg-red-500' : 'bg-green-400'
          }`} />
        </div>
      </div>

      {/* Content */}
      <div className="mt-2 space-y-2">
        {config.prompt && (
          <div className={`text-xs ${colors.text.secondary}`}>
            Prompt: {config.prompt.length > 50 ? `${config.prompt.substring(0, 50)}...` : config.prompt}
          </div>
        )}
        
        <div className={`flex justify-between text-xs ${colors.text.secondary}`}>
          <span>Tokens: {config.maxTokens || 1000}</span>
          {config.temperature && <span>Temp: {config.temperature}</span>}
        </div>
      </div>

      {/* Error message */}
      {data.hasError && data.errorMessage && (
        <div className="mt-2 text-xs text-red-200 bg-red-500 bg-opacity-20 px-2 py-1 rounded">
          {data.errorMessage}
        </div>
      )}

      {/* Execution data preview */}
      {data.executionData && (
        <div className={`mt-2 text-xs ${colors.text.secondary} bg-black bg-opacity-20 px-2 py-1 rounded`}>
          <div className="font-mono">
            {typeof data.executionData === 'object' 
              ? JSON.stringify(data.executionData, null, 2).substring(0, 50) + '...'
              : String(data.executionData).substring(0, 50)
            }
          </div>
        </div>
      )}

      <Handle
        type="source"
        position={Position.Right}
        className={`w-3 h-3 bg-white border-2 ${colors.handle}`}
      />
    </div>
  );
};

export default ClaudeChatNode;
